import React from "react";
import _ from "lodash";
import { useIntl } from "react-intl";
import { ColumnDefinition, RowDefinition } from "griddle-react";
import { Link } from "react-router-dom";
import { connect } from "react-redux";

import { AuthState } from "@auth/models";
import { Table, TableProps } from "@ui/Table";
import { Collapsable } from "@ui/Table/Collapsable";
import { CellDateTime, CellBasicNumber } from "@ui/Table/Cells";
import { canViewSales as canViewSalesSelector } from "@sales/selectors";
import { messages as sMessages } from "@sales/messages";
import { compose } from "@util/compose";
import { Playlist, PlaylistTrack } from "../../models";
import { CellNumber, CellTrackLink, CellVendorLink } from "../TrackListTable";
import { messages } from "../../messages";

interface PlaylistTrackListTableProps {
    playlistTracks: PlaylistTrack[];
    isPlaylist?: boolean;
    collapsable?: boolean;
}

interface PlaylistTrackListTableStateProps {
    canViewSales: boolean;
}

interface Row {
    entity: PlaylistTrack;
}

// Type the table component
type TypedTable = new () => Table<Row>;
const TypedTable = Table as TypedTable;

type Props = PlaylistTrackListTableProps & PlaylistTrackListTableStateProps;

const TRACKS_LIMIT = 7;

const CellPlaylistLink = ({ value }: { value: Playlist }) => {
    const playlist = new Playlist(value.toJS());
    let elt = <span>{playlist.name}</span>;
    if (playlist.viewUrl != null) {
        elt = <Link to={playlist.viewUrl}>{playlist.name}</Link>;
    }
    return elt;
};

const CellNumberStreams = ({ value }: { value: number | null }) => (
    <span>
        {value == null && "-"}
        {value != null && <CellBasicNumber value={value} />}
    </span>
);

const PlaylistTrackListTable = ({ playlistTracks, isPlaylist, collapsable, canViewSales }: Props) => {
    const intl = useIntl();
    const data = _.map(playlistTracks, (track) => {
        return {entity: track} as Row;
    });
    const tableProps: TableProps<Row> = {
        data,
        pageSize: data.length,
        layout: "basic",
        size: "small",
    };
    let hasAddedAt = false;
    if (playlistTracks.length > 0) {
        hasAddedAt = playlistTracks[0].track.pivot != null && playlistTracks[0].track.pivot.addedAt != null;
    }

    const tableDefinition = (
        <RowDefinition cssClassName={
            ({ rowData }) => rowData.entity.track.trackId != null && isPlaylist
                ? "table-primary"
                : ""
        }>
            {isPlaylist &&
                <ColumnDefinition
                    key="number"
                    id="entity.track.pivot.order"
                    title="#"
                    customComponent={CellNumber}
                />
            }
            <ColumnDefinition
                key="title"
                id="entity.track.title"
                title={intl.formatMessage(messages.trackColumnTitle)}
            />
            <ColumnDefinition
                key="artists"
                id="entity.track.artists"
                title={intl.formatMessage(messages.trackColumnArtists)}
            />
            <ColumnDefinition
                key="album"
                id="entity.track.album"
                title={intl.formatMessage(messages.trackColumnAlbum)}
            />
            <ColumnDefinition
                key="isrc"
                id="entity.track.isrc"
                title={intl.formatMessage(messages.trackColumnIsrc)}
            />
            {isPlaylist && hasAddedAt &&
                <ColumnDefinition
                    key="addedAt"
                    id="entity.track.pivot.addedAt"
                    title={intl.formatMessage(messages.playlistSnapshotTrackColumnAddedAt)}
                    customComponent={CellDateTime}
                />
            }
            {!isPlaylist &&
                <ColumnDefinition
                    key="playlist"
                    id="entity.playlist"
                    title={intl.formatMessage(messages.playlistEntitySingular)}
                    customComponent={CellPlaylistLink}
                />
            }
            <ColumnDefinition
                key="vendor"
                id="entity.track"
                title={intl.formatMessage(messages.trackColumnVendor)}
                customComponent={CellVendorLink}
            />
            {canViewSales &&
                <ColumnDefinition
                    key="streams"
                    id="entity.streams"
                    title={intl.formatMessage(sMessages.numStreams)}
                    customComponent={CellNumberStreams}
                />
            }
            <ColumnDefinition
                key="buttons"
                id="entity.track.trackId"
                title=" "
                customComponent={CellTrackLink}
            />
        </RowDefinition>
    );

    return (
        <div className="vendor-track-list">
            {!collapsable && <Table {...tableProps}>{tableDefinition}</Table>}
            {collapsable && <Collapsable<Row> limit={TRACKS_LIMIT} {...tableProps}>{tableDefinition}</Collapsable>}
        </div>
    );
};

const mapStateToProps = (state: AuthState) => ({
    canViewSales: canViewSalesSelector(state),
});

const ConnectedPlaylistTrackListTable = compose<Props, PlaylistTrackListTableProps>(
    connect(mapStateToProps, null),
)(PlaylistTrackListTable);

export {
    PlaylistTrackListTable as PlaylistTrackListTablePresenter,
    ConnectedPlaylistTrackListTable as PlaylistTrackListTable,
    Props as PlaylistTrackListTableProps,
    CellPlaylistLink,
    CellNumberStreams,
};
